import {
  type FieldCatalogue,
  type FindingView,
  isRequesterFinding,
  REQUESTER_FINDING_FIELDS,
} from "@procuremate/shared";
import { ListChecks } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import type { Language } from "@/i18n";
import { useLeaveFinding } from "./api";
import { withLabels } from "./field-format";
import { usePanelActions } from "./panel-actions";

/**
 * "Before you send" (WP-2.8, ADR-0030): the open points the requester can settle themselves — the
 * server sends no others. Each can be fixed (its field's editor, or a message in the conversation)
 * or left to procurement; points left to procurement can be taken back.
 */
export function BeforeYouSend({
  requestId,
  findings,
  catalogue,
  canAct,
  busy,
  onFixField,
  missing = [],
}: {
  requestId: string;
  findings: FindingView[];
  catalogue: FieldCatalogue | undefined;
  /** The requester of an open request. */
  canAct: boolean;
  /** A reply is being written: changes wait. */
  busy: boolean;
  /** Opens the editor of the field that settles a point. */
  onFixField: (fieldKey: string) => void;
  /** WP-2.9 (Q11 option C): required details still missing, shown before Send. */
  missing?: { key: string; label: string }[];
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.language as Language;
  const titleId = useId();
  const actions = usePanelActions();
  const leave = useLeaveFinding(requestId);
  const points = findings.filter((f) => f.resolvedAt === null && isRequesterFinding(f.type));
  const open = points.filter((f) => f.leftToBuyerAt === null);
  const left = points.filter((f) => f.leftToBuyerAt !== null);
  if (points.length === 0 && missing.length === 0) return null;

  const editable = new Set(catalogue?.fields.filter((f) => f.editable).map((f) => f.key));
  function fix(f: FindingView) {
    const key = isRequesterFinding(f.type) ? REQUESTER_FINDING_FIELDS[f.type] : null;
    if (key && editable.has(key)) onFixField(key);
    else
      actions?.draftMessage(
        t("requests.panel.beforeSend.draft", { point: withLabels(f.message, catalogue, language) }),
      );
  }

  return (
    <section aria-labelledby={titleId} className="rounded-lg border-l-4 border-l-finding bg-surface p-4 shadow-card">
      <h3 id={titleId} className="flex items-center gap-2 font-heading text-base font-semibold text-fg">
        <ListChecks aria-hidden="true" className="h-5 w-5 text-finding" />
        {t("requests.panel.beforeSend.title")}
      </h3>
      {missing.length > 0 ? (
        <>
          <p className="mt-1 text-sm text-fg-muted">{t("requests.panel.beforeSend.missingIntro")}</p>
          <ul className="mt-1 flex flex-col divide-y divide-border">
            {missing.map((m) => (
              <li key={m.key} className="flex items-center justify-between gap-2 py-2">
                <p className="min-w-0 text-sm font-medium break-words text-fg">{m.label}</p>
                {canAct ? (
                  <Button
                    className="h-8 shrink-0 px-2.5"
                    disabled={busy}
                    aria-label={t("requests.panel.beforeSend.fixNamed", { point: m.label })}
                    onClick={() => onFixField(m.key)}
                  >
                    {t("requests.panel.beforeSend.fix")}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {open.length > 0 ? (
        <>
          <p className="mt-1 text-sm text-fg-muted">{t("requests.panel.beforeSend.intro")}</p>
          <ul className="mt-2 flex flex-col divide-y divide-border">
            {open.map((f) => {
              const point = withLabels(f.message, catalogue, language);
              return (
                <li key={f.id} className="py-2.5">
                  <p className="text-sm break-words text-fg">{point}</p>
                  {canAct ? (
                    <div className="mt-1.5 flex flex-wrap gap-2">
                      <Button
                        className="h-8 px-2.5"
                        disabled={busy}
                        aria-label={t("requests.panel.beforeSend.fixNamed", { point })}
                        onClick={() => fix(f)}
                      >
                        {t("requests.panel.beforeSend.fix")}
                      </Button>
                      <Button
                        variant="secondary"
                        className="h-8 px-2.5"
                        disabled={busy}
                        aria-disabled={leave.isPending || undefined}
                        aria-label={t("requests.panel.beforeSend.leaveNamed", { point })}
                        onClick={() => !leave.isPending && leave.mutate({ findingId: f.id, leftToBuyer: true })}
                      >
                        {t("requests.panel.beforeSend.leave")}
                      </Button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
      {left.length > 0 ? (
        <details className="mt-2">
          <summary className="cursor-pointer text-sm font-semibold text-link">
            {t("requests.panel.beforeSend.left", { count: left.length })}
          </summary>
          <ul className="mt-1 flex flex-col divide-y divide-border">
            {left.map((f) => {
              const point = withLabels(f.message, catalogue, language);
              return (
                <li key={f.id} className="flex items-start justify-between gap-2 py-2">
                  <p className="min-w-0 text-sm break-words text-fg-muted">{point}</p>
                  {canAct ? (
                    <Button
                      variant="ghost"
                      className="h-8 shrink-0 px-2"
                      disabled={busy}
                      aria-label={t("requests.panel.beforeSend.takeBackNamed", { point })}
                      onClick={() => !leave.isPending && leave.mutate({ findingId: f.id, leftToBuyer: false })}
                    >
                      {t("requests.panel.beforeSend.takeBack")}
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </details>
      ) : null}
      {leave.isError ? (
        <p role="alert" className="mt-2 text-sm text-danger">
          {t("requests.panel.beforeSend.error")}
        </p>
      ) : null}
    </section>
  );
}
